import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { db, auth } from "../Firebase";
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL
} from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import { toast } from "react-toastify";
import "../styles/UploadVideo.css";

const MAX_VIDEOS = 5;

const UploadVideo = () => {
  const { id } = useParams(); // courseId
  const navigate = useNavigate();
  const storage = getStorage();

  const [checkingAuth, setCheckingAuth] = useState(true);
  const [uploading, setUploading] = useState(false);

  // First video = sample (preview)
  const [videos, setVideos] = useState([
    {
      title: "",
      description: "",
      file: null,
      isSample: true,
      duration: 0
    }
  ]);

  /* ================= AUTH CHECK ================= */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async user => {
      if (!user) return navigate("/login");

      const snap = await getDoc(doc(db, "courses", id));
      if (!snap.exists()) return navigate("/teacherCourse");

      if (snap.data().instructorId !== user.uid) {
        toast.error("Unauthorized access");
        navigate("/teacherCourse");
        return;
      }

      setCheckingAuth(false);
    });

    return () => unsub();
  }, [navigate, id]);

  if (checkingAuth) {
    return <p style={{ padding: 40 }}>Checking access...</p>;
  }

  /* ================= HELPERS ================= */
  const formatDuration = totalSec => {
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = Math.floor(totalSec % 60);

    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  const addVideoField = () => {
    if (videos.length >= MAX_VIDEOS) {
      toast.warning("Maximum 5 videos allowed");
      return;
    }

    setVideos([
      ...videos,
      {
        title: "",
        description: "",
        file: null,
        isSample: false,
        duration: 0
      }
    ]);
  };

  const updateVideo = (index, field, value) => {
    const copy = [...videos];
    copy[index][field] = value;
    setVideos(copy);
  };

  /* ================= UPLOAD ================= */
  const uploadAll = async () => {
    // Sample validation
    if (!videos[0].title || !videos[0].file) {
      toast.error("Sample video is mandatory");
      return;
    }

    // Other videos validation
    if (
      videos.slice(1).some(v => !v.title || !v.file)
    ) {
      toast.error("All videos must have title & file");
      return;
    }

    try {
      setUploading(true);

      let totalDuration = 0;
      const uploadedVideos = [];

      for (let i = 0; i < videos.length; i++) {
        const v = videos[i];

        const storageRef = ref(
          storage,
          `course-videos/${id}/${Date.now()}-${v.file.name}`
        );

        await uploadBytes(storageRef, v.file);
        const url = await getDownloadURL(storageRef);

        // count duration ONLY for course videos
        if (!v.isSample) {
          totalDuration += Math.floor(v.duration);
        }

        uploadedVideos.push({
          id: `v${i + 1}`,
          title: v.title,
          url,
          isSample: v.isSample
        });
      }


      // Update course document
      await updateDoc(doc(db, "courses", id), {
        previewVideo: uploadedVideos[0].url,
        videos: uploadedVideos,
        duration: formatDuration(totalDuration),
        durationSeconds: totalDuration, //
        updatedAt: serverTimestamp()
      });


      toast.success("Videos uploaded successfully");
      navigate("/teacherCourse");
    } catch (err) {
      console.error(err);
      toast.error("Video upload failed");
    } finally {
      setUploading(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="upload-page">
      <h1>Upload Course Videos</h1>

      {/* SAMPLE VIDEO */}
      <div className="video-card sample">
        <div className="video-header">
          <h3>Sample Video (Mandatory)</h3>
          <span className="sample-badge">Preview</span>
        </div>

        <input
          placeholder="Sample video title"
          value={videos[0].title}
          onChange={e =>
            updateVideo(0, "title", e.target.value)
          }
        />

        <textarea
          placeholder="Sample video description"
          value={videos[0].description}
          onChange={e =>
            updateVideo(0, "description", e.target.value)
          }
        />

        <input
          type="file"
          accept="video/*"
          onChange={e =>
            updateVideo(0, "file", e.target.files[0])
          }
        />

        {videos[0].file && (
          <video
            src={URL.createObjectURL(videos[0].file)}
            onLoadedMetadata={e =>
              updateVideo(0, "duration", e.target.duration)
            }
            style={{ display: "none" }}
          />
        )}
      </div>

      {/* COURSE VIDEOS */}
      {videos.slice(1).map((v, index) => (
        <div key={index + 1} className="video-card">
          <h3>Course Video {index + 1}</h3>

          <input
            placeholder="Video title"
            value={v.title}
            onChange={e =>
              updateVideo(index + 1, "title", e.target.value)
            }
          />

          <textarea
            placeholder="Description"
            value={v.description}
            onChange={e =>
              updateVideo(
                index + 1,
                "description",
                e.target.value
              )
            }
          />

          <input
            type="file"
            accept="video/*"
            onChange={e =>
              updateVideo(
                index + 1,
                "file",
                e.target.files[0]
              )
            }
          />

          {v.file && (
            <video
              src={URL.createObjectURL(v.file)}
              onLoadedMetadata={e =>
                updateVideo(
                  index + 1,
                  "duration",
                  e.target.duration
                )
              }
              style={{ display: "none" }}
            />
          )}
        </div>
      ))}

      <button
        className="add-btn"
        onClick={addVideoField}
        disabled={videos.length >= MAX_VIDEOS}
      >
        + Add Course Video
      </button>

      <div className="action-row">
        <button
          className="cancel"
          onClick={() => navigate("/teacherCourse")}
        >
          Cancel
        </button>

        <button onClick={uploadAll} disabled={uploading}>
          {uploading ? "Uploading..." : "Upload Videos"}
        </button>
      </div>
    </div>
  );
};

export default UploadVideo;
